import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Stethoscope, ArrowLeft, Video, Phone } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const BookingPage = () => {
  const { doctorId } = useParams();
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [doctor, setDoctor] = useState(null);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [bookingData, setBookingData] = useState({
    consultation_type: 'video',
    appointment_date: '',
    appointment_time: '10:00'
  });

  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);

  useEffect(() => {
    if (!bookingData.appointment_date) {
      setAvailableSlots([]);
      return;
    }
    setLoadingSlots(true);
    axios.get(`${API}/doctors/${doctorId}/available-slots?date=${bookingData.appointment_date}`)
      .then(r => {
        setAvailableSlots(r.data.available_slots || []);
        if (r.data.available_slots?.length > 0 && !r.data.available_slots.includes(bookingData.appointment_time)) {
          setBookingData(b => ({ ...b, appointment_time: r.data.available_slots[0] }));
        }
      })
      .catch(() => setAvailableSlots([]))
      .finally(() => setLoadingSlots(false));
  }, [bookingData.appointment_date, doctorId]);

  const [medicalForm, setMedicalForm] = useState({
    date_of_birth: '',
    gender: 'male',
    current_symptoms: '',
    medical_conditions: '',
    allergies: '',
    current_medications: '',
    additional_notes: ''
  });

  useEffect(() => {
    if (user === false) {
      navigate('/login');
      return;
    }
    if (!user) return;
    
    const fetchDoctor = async () => {
      try {
        const response = await axios.get(`${API}/doctors/${doctorId}`);
        setDoctor(response.data);
      } catch (err) {
        setError(t('لم يتم العثور على الطبيب', 'Doctor not found'));
      }
    };
    fetchDoctor();
  }, [doctorId, user, navigate, t]);

  const handleSubmitMedicalForm = (e) => {
    e.preventDefault();
    if (!medicalForm.date_of_birth || !medicalForm.current_symptoms) {
      setError(t('يرجى إكمال الحقول المطلوبة', 'Please complete required fields'));
      return;
    }
    setError('');
    setStep(3);
  };

  const handleConfirmBooking = async () => {
    setLoading(true);
    setError('');

    try {
      await axios.post(
        `${API}/appointments`,
        {
          doctor_id: doctorId,
          appointment_date: bookingData.appointment_date,
          appointment_time: bookingData.appointment_time,
          consultation_type: bookingData.consultation_type,
          medical_form: medicalForm
        },
        { withCredentials: true }
      );

      setSuccess(t('تم حجز الموعد بنجاح', 'Appointment booked successfully'));
      setTimeout(() => navigate('/patient'), 2000);
    } catch (err) {
      setError(err.response?.data?.detail || t('حدث خطأ', 'An error occurred'));
    } finally {
      setLoading(false);
    }
  };

  if (!doctor) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1e88e5]"></div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-gray-50 py-8 ${language === 'ar' ? 'rtl' : 'ltr'}`}>
      <div className="container mx-auto px-4 max-w-3xl">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-4"
          data-testid="back-button"
        >
          <ArrowLeft className={language === 'ar' ? 'ml-2 rotate-180' : 'mr-2'} size={20} />
          {t('رجوع', 'Back')}
        </Button>

        <Card className="p-6 mb-6">
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center">
              <Stethoscope className="text-[#1e88e5]" size={40} />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-[#2c3e50]">{doctor.full_name}</h2>
              <p className="text-[#1e88e5] font-semibold">{doctor.specialty}</p>
            </div>
          </div>
        </Card>

        <div className="flex justify-between mb-8">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex-1 text-center">
              <div className={`w-10 h-10 rounded-full mx-auto flex items-center justify-center font-bold ${
                step >= s ? 'bg-[#1e88e5] text-white' : 'bg-gray-200 text-gray-500'
              }`}>
                {s}
              </div>
              <p className="text-sm mt-2">
                {s === 1 && t('نوع الاستشارة', 'Type')}
                {s === 2 && t('النموذج الطبي', 'Medical Form')}
                {s === 3 && t('تأكيد', 'Confirm')}
              </p>
            </div>
          ))}
        </div>

        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {success && (
          <Alert className="mb-4 bg-green-50 border-green-200">
            <AlertDescription className="text-green-800">{success}</AlertDescription>
          </Alert>
        )}

        {step === 1 && (
          <Card className="p-6" data-testid="booking-step-1">
            <h3 className="text-xl font-bold mb-4">{t('اختر نوع الاستشارة', 'Choose Consultation Type')}</h3>
            
            <RadioGroup
              value={bookingData.consultation_type}
              onValueChange={(value) => setBookingData({ ...bookingData, consultation_type: value })}
              className="space-y-3 mb-6"
            >
              <label className="flex items-center gap-3 p-4 border rounded-lg cursor-pointer hover:bg-blue-50">
                <RadioGroupItem value="video" />
                <Video size={20} className="text-[#1e88e5]" />
                <span className="font-semibold">{t('استشارة عبر الفيديو', 'Video Consultation')}</span>
              </label>
              <label className="flex items-center gap-3 p-4 border rounded-lg cursor-pointer hover:bg-blue-50">
                <RadioGroupItem value="voice" />
                <Phone size={20} className="text-[#1e88e5]" />
                <span className="font-semibold">{t('استشارة صوتية', 'Voice Consultation')}</span>
              </label>
            </RadioGroup>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <Label>{t('التاريخ', 'Date')}</Label>
                <Input
                  type="date"
                  value={bookingData.appointment_date}
                  onChange={(e) => setBookingData({ ...bookingData, appointment_date: e.target.value })}
                  min={new Date().toISOString().split('T')[0]}
                  required
                  className="mt-2"
                  data-testid="appointment-date"
                />
              </div>
              <div>
                <Label>{t('الوقت المتاح', 'Available Time')}</Label>
                {bookingData.appointment_date ? (
                  loadingSlots ? (
                    <p className="text-sm text-gray-500 mt-3">{t('جاري تحميل الأوقات...', 'Loading available times...')}</p>
                  ) : availableSlots.length === 0 ? (
                    <p className="text-sm text-gray-500 mt-3">{t('لا توجد أوقات متاحة في هذا اليوم', 'No times available on this day')}</p>
                  ) : (
                    <div className="flex flex-wrap gap-2 mt-2" data-testid="time-slots">
                      {availableSlots.map((slot) => (
                        <button
                          key={slot}
                          type="button"
                          onClick={() => setBookingData({ ...bookingData, appointment_time: slot })}
                          className={`px-3 py-2 rounded-md text-sm font-semibold border transition ${
                            bookingData.appointment_time === slot
                              ? 'bg-[#1e88e5] text-white border-[#1e88e5]'
                              : 'bg-white text-[#2c3e50] border-gray-300 hover:border-[#1e88e5]'
                          }`}
                          data-testid={`slot-${slot}`}
                        >
                          {slot}
                        </button>
                      ))}
                    </div>
                  )
                ) : (
                  <p className="text-sm text-gray-500 mt-3">{t('اختر التاريخ أولاً', 'Pick a date first')}</p>
                )}
              </div>
            </div>

            <Button
              onClick={() => {
                if (!bookingData.appointment_date) {
                  setError(t('يرجى اختيار تاريخ', 'Please select a date'));
                  return;
                }
                if (availableSlots.length > 0 && !availableSlots.includes(bookingData.appointment_time)) {
                  setError(t('يرجى اختيار وقت متاح', 'Please pick an available time slot'));
                  return;
                }
                setError('');
                setStep(2);
              }}
              className="w-full bg-[#1e88e5] hover:bg-[#1976d2]"
              data-testid="next-to-medical-form"
            >
              {t('التالي', 'Next')}
            </Button>
          </Card>
        )}

        {step === 2 && (
          <Card className="p-6" data-testid="booking-step-2">
            <h3 className="text-xl font-bold mb-2">{t('النموذج الطبي', 'Medical Information Form')}</h3>
            <p className="text-sm text-gray-600 mb-6">
              {t('هذه المعلومات تشارك فقط مع الطبيب المختص', 'Information shared only with assigned doctor')}
            </p>

            <form onSubmit={handleSubmitMedicalForm} className="space-y-4">
              <div>
                <Label>{t('تاريخ الميلاد', 'Date of Birth')} <span className="text-red-500">*</span></Label>
                <Input
                  type="date"
                  value={medicalForm.date_of_birth}
                  onChange={(e) => setMedicalForm({ ...medicalForm, date_of_birth: e.target.value })}
                  required
                  className="mt-2"
                  data-testid="medical-dob"
                />
              </div>

              <div>
                <Label>{t('الجنس', 'Gender')} <span className="text-red-500">*</span></Label>
                <Select
                  value={medicalForm.gender}
                  onValueChange={(value) => setMedicalForm({ ...medicalForm, gender: value })}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">{t('ذكر', 'Male')}</SelectItem>
                    <SelectItem value="female">{t('أنثى', 'Female')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>{t('الأعراض الحالية', 'Current Symptoms')} <span className="text-red-500">*</span></Label>
                <Textarea
                  value={medicalForm.current_symptoms}
                  onChange={(e) => setMedicalForm({ ...medicalForm, current_symptoms: e.target.value })}
                  placeholder={t('اشرح أعراضك الحالية', 'Describe your symptoms')}
                  required
                  rows={3}
                  className="mt-2"
                  data-testid="medical-symptoms"
                />
              </div>

              <div>
                <Label>{t('الحالات المرضية الموجودة (اختياري)', 'Existing Medical Conditions (Optional)')}</Label>
                <Textarea
                  value={medicalForm.medical_conditions}
                  onChange={(e) => setMedicalForm({ ...medicalForm, medical_conditions: e.target.value })}
                  rows={2}
                  className="mt-2"
                />
              </div>

              <div>
                <Label>{t('الحساسية (اختياري)', 'Allergies (Optional)')}</Label>
                <Input
                  value={medicalForm.allergies}
                  onChange={(e) => setMedicalForm({ ...medicalForm, allergies: e.target.value })}
                  className="mt-2"
                />
              </div>

              <div>
                <Label>{t('الأدوية الحالية (اختياري)', 'Current Medications (Optional)')}</Label>
                <Input
                  value={medicalForm.current_medications}
                  onChange={(e) => setMedicalForm({ ...medicalForm, current_medications: e.target.value })}
                  className="mt-2"
                />
              </div>

              <div>
                <Label>{t('ملاحظات إضافية (اختياري)', 'Additional Notes (Optional)')}</Label>
                <Textarea
                  value={medicalForm.additional_notes}
                  onChange={(e) => setMedicalForm({ ...medicalForm, additional_notes: e.target.value })}
                  rows={2}
                  className="mt-2"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setStep(1)} className="flex-1">
                  {t('السابق', 'Previous')}
                </Button>
                <Button type="submit" className="flex-1 bg-[#1e88e5] hover:bg-[#1976d2]" data-testid="submit-medical-form">
                  {t('التالي', 'Next')}
                </Button>
              </div>
            </form>
          </Card>
        )}

        {step === 3 && (
          <Card className="p-6" data-testid="booking-step-3">
            <h3 className="text-xl font-bold mb-4">{t('مراجعة الحجز', 'Review Booking')}</h3>

            <div className="space-y-3 mb-6">
              <div className="flex justify-between border-b pb-2">
                <span className="text-gray-600">{t('الطبيب', 'Doctor')}</span>
                <span className="font-semibold">{doctor.full_name}</span>
              </div>
              <div className="flex justify-between border-b pb-2">
                <span className="text-gray-600">{t('التخصص', 'Specialty')}</span>
                <span className="font-semibold">{doctor.specialty}</span>
              </div>
              <div className="flex justify-between border-b pb-2">
                <span className="text-gray-600">{t('نوع الاستشارة', 'Type')}</span>
                <span className="font-semibold">
                  {bookingData.consultation_type === 'video'
                    ? t('فيديو', 'Video')
                    : t('صوتية', 'Voice')}
                </span>
              </div>
              <div className="flex justify-between border-b pb-2">
                <span className="text-gray-600">{t('التاريخ', 'Date')}</span>
                <span className="font-semibold">{bookingData.appointment_date}</span>
              </div>
              <div className="flex justify-between border-b pb-2">
                <span className="text-gray-600">{t('الوقت', 'Time')}</span>
                <span className="font-semibold">{bookingData.appointment_time}</span>
              </div>
            </div>

            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setStep(2)} className="flex-1" disabled={loading}>
                {t('السابق', 'Previous')}
              </Button>
              <Button
                onClick={handleConfirmBooking}
                disabled={loading}
                className="flex-1 bg-[#1e88e5] hover:bg-[#1976d2]"
                data-testid="confirm-booking"
              >
                {loading ? t('جاري الحجز...', 'Booking...') : t('تأكيد الحجز', 'Confirm Booking')}
              </Button>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};
